import * as React from "react";
import * as S from "@styled";

import useUserData from "hooks/useUserData";
import useDeferredMutation from "hooks/useDeferredMutation";
import ContainerWithLoadingSpinner from "components/shared/ContainerWithLoadingSpinner";
import Text from "components/shared/Text";
import ConfirmDeleteModal from "components/settings/ConfirmDeleteModal";
import { ProcessCounts } from "model/process/Process";
import ProcessList from "./IntegrationList";

export const PROCESSES_APIROUTE = "processes";

const MyIntegrations: React.FC = () => {
    const { selectedOrganization } = useUserData();
    const [
        openConfirmDeleteModalForProcessId,
        setOpenConfirmDeleteModalForProcessId,
    ] = React.useState<number | undefined>();

    const deferredCountsFetch = useDeferredMutation<ProcessCounts>("GET");

    const fetchProcessData = () => {
        deferredCountsFetch.reset();
        deferredCountsFetch.mutate({
            url: `${PROCESSES_APIROUTE}/all-counts`,
        });
    };

    React.useEffect(() => {
        fetchProcessData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrganization]);

    const deferredProcessDelete = useDeferredMutation(
        "DELETE",
        () => {
            setOpenConfirmDeleteModalForProcessId(undefined);
            fetchProcessData();
        },
        "Failed to delete process.",
    );

    const handleDeleteProcess = () => {
        deferredProcessDelete.mutate({
            url: `${PROCESSES_APIROUTE}/${openConfirmDeleteModalForProcessId}`,
        });
    };

    const handleDeleteClick = (processId: number) =>
        setOpenConfirmDeleteModalForProcessId(processId);

    return (
        <div>
            <S.MyIntegrationsTitleContainer>
                <Text tag="h1">My integrations</Text>
            </S.MyIntegrationsTitleContainer>

            <ProcessesExecutionCounts
                counts={deferredCountsFetch.data}
                isLoading={deferredCountsFetch.isLoading}
            />

            <ProcessList
                organization={selectedOrganization!}
                onProcessDeleteClick={handleDeleteClick}
            />

            {openConfirmDeleteModalForProcessId !== undefined && (
                <ConfirmDeleteModal
                    title="Do you really want to delete this process?"
                    onClose={() =>
                        setOpenConfirmDeleteModalForProcessId(undefined)
                    }
                    onDelete={handleDeleteProcess}
                    deleting={deferredProcessDelete.isLoading}
                    deleteError={deferredProcessDelete.isError}
                />
            )}
        </div>
    );
};

interface ProcessesExecutionCountsProps {
    isLoading: boolean;
    counts?: ProcessCounts;
}

const ProcessesExecutionCounts: React.FC<ProcessesExecutionCountsProps> = (
    props,
) => {
    const getSuccessRatio = () => {
        if (successes === undefined || executions === undefined) {
            return undefined;
        }

        // Cant calculate ratio if both are 0
        if (successes === 0 && executions === 0) {
            return undefined;
        }

        return `${Number((successes / executions) * 100).toFixed(1)}%`;
    };

    const counts = props.counts;
    const processes = counts?.processesCount;
    const executions = counts?.totalExecutionCount;
    const errors = counts?.failedExecutionCount;
    const successes = counts?.successfulExecutionCount;
    const successRatio = getSuccessRatio();

    return (
        <S.MyIntegrationsKPIContainer>
            <Text>Last 7 days</Text>

            <ContainerWithLoadingSpinner
                loading={props.isLoading}
                style={{ minHeight: "7.438rem" }}
            >
                <CountLarge value={processes} label="Processes" />

                <CountLarge value={executions} label="Executions" />

                <CountLarge value={errors} label="Errors" />

                <CountLarge value={successRatio} label="Success" />
            </ContainerWithLoadingSpinner>
        </S.MyIntegrationsKPIContainer>
    );
};

export interface CountProps {
    value?: string | number;
    label: string;
}

const CountLarge: React.FC<CountProps> = ({ value = "-", label }) => (
    <S.MyIntegrationsCountLarge>
        <Text tag="span" size="bold">
            {value}
        </Text>

        <br />

        <Text tag="span" appearance="h2">
            {label}
        </Text>
    </S.MyIntegrationsCountLarge>
);

export default MyIntegrations;
